import { plainDtos } from '@abg/shared';

import { transformChildSeatItemsForPriceDetails } from '../../shared/utils/ancillaries';

import { CarInfoSavingsParams, CostListType } from './car-info-accordion.types';

type PriceSummaryData = plainDtos.ExperienceAvailabilityCalculatePriceOutputDto;

export const accordionSummaryClass: AccordionSummaryClassHelper =
  (className) => (activeSummaryClass) =>
    `${className} ${activeSummaryClass || ''}`;

type AccordionSummaryClassHelper = (
  className: string,
) => (activeSummaryClass: string | undefined) => string;

export const makeSavingsList = (
  pricingData: PriceSummaryData | null,
  t: (value: string) => string,
  i18nKeys: Record<string, string>,
): CarInfoSavingsParams[] => {
  const propertyList = Object.keys(pricingData?.savings || {}) as Array<
    keyof PriceSummaryData['savings']
  >;

  return propertyList.reduce(
    (accumulator: CarInfoSavingsParams[], savingKey) => {
      // Skip excluded keys
      if (
        [
          'totalSavings',
          'currencySymbol',
          'currencyCode',
          'payNowSavingsPotential',
        ].includes(savingKey)
      ) {
        return accumulator;
      }

      const amount = Number(pricingData?.savings[savingKey] || 0);

      // Skip if amount is 0
      if (amount === 0) {
        return accumulator;
      }

      // Build the savings list
      return [
        ...accumulator,
        {
          description: t(i18nKeys[savingKey] as string),
          amount: amount,
        },
      ];
    },
    [],
  );
};

export const getAncillariesCostList = (
  pricingData: PriceSummaryData | null,
): CostListType[] => {
  if (!pricingData) return [];

  const { protectionItems, protectionBundle, addOnItems, addOnBundle } =
    pricingData;

  // for EMEA, bundle will be shown instead of list of included items
  const visibleBundles = [protectionBundle, addOnBundle].filter((bundle) =>
    bundle?.items?.some((item) => item.tier),
  );

  // to avoid showing individual items that are already included in visible bundles
  const itemsIncludedInVisibleBundles = new Set(
    visibleBundles.flatMap(
      (bundle) => bundle?.items?.map((item) => item.code) || [],
    ),
  );

  const itemsIncludedInBundles = new Set(
    [protectionBundle, addOnBundle].flatMap(
      (bundle) => bundle?.items?.map((item) => item.code) || [],
    ),
  );

  const individualItems = [
    ...(protectionItems || []),
    ...(addOnItems || []),
  ].filter((item) => !itemsIncludedInVisibleBundles.has(item.code));

  // Bundles that have tier data and are shown as a single bundle line item (EMEA standard)
  const mappedBundles = visibleBundles.map((bundle) => {
    const previousCost =
      !!bundle?.grossSubtotal && bundle.grossSubtotal > bundle.netSubtotal
        ? bundle.grossSubtotal
        : undefined;

    return {
      previousCost,
      code: bundle?.code,
      description: bundle?.code || '',
      amount: bundle?.netTotal || 0,
      removable: false,
    };
  }) as CostListType[];

  // ARGWS-2493: a bundle row must only be emitted when the price genuinely lives on
  // the bundle alone. If any of its items is already priced in the individual list,
  // the row duplicates them and the list stops reconciling with the header total.
  const isPricedIndividually = (
    bundle?: PriceSummaryData['protectionBundle'],
  ) =>
    // NB: bundle.items[].amount is the excess coverage figure, not a price — the
    // price has to be looked up on the individual item.
    bundle?.items?.some((bundleItem) =>
      individualItems.some(
        (item) => item.code === bundleItem.code && Number(item.amount ?? 0) > 0,
      ),
    ) ?? false;

  // Bundles without tier data but with a non-zero netTotal (AF upgrade flow):
  // These are not in visibleBundles, so their included items aren't filtered out above.
  // However, the individual protectionItems for these bundle items come back with amount=0
  // (the price is only on the bundle itself). Show the bundle as a line item so the
  // price is not lost in the footer.
  const nonVisibleBundlesWithPrice = [protectionBundle, addOnBundle]
    .filter(
      (bundle) =>
        bundle &&
        bundle.netTotal &&
        bundle.netTotal > 0 &&
        !visibleBundles.includes(bundle) &&
        !isPricedIndividually(bundle),
    )
    .map((bundle) => ({
      code: bundle?.code,
      description: bundle?.code || '',
      amount: bundle?.netTotal || 0,
      removable: false,
    })) as CostListType[];

  const mappedIndividualItems = transformChildSeatItemsForPriceDetails(
    individualItems,
    itemsIncludedInBundles,
  ).map((item) => {
    let removable = !itemsIncludedInBundles.has(item.code); // if item is included in bundle, it can`t be removed separately

    // Seems like for avis first cars we got those props we can use to
    // to hide remove cross.
    if (item.displayElement?.editable !== undefined) {
      removable = item.displayElement?.editable;
    }

    // Avis First included items (AFHFF fuel, AFE experience) are not
    // user-removable. The response does not always carry
    // displayElement.editable for them, so enforce it by code to avoid
    // rendering a remove cross on an included product.
    if (item.code === 'AFHFF' || item.code === 'AFE') {
      removable = false;
    }

    return {
      ...item,
      removable,
    };
  }) as CostListType[];

  return [
    ...mappedBundles,
    ...nonVisibleBundlesWithPrice,
    ...mappedIndividualItems,
  ];
};

export const getAncillariesCostListNam = (
  pricingData: PriceSummaryData | null,
): CostListType[] => {
  if (!pricingData) return [];

  const { protectionItems, protectionBundle, addOnItems, addOnBundle } =
    pricingData;

  const itemsIncludedInBundles = new Set(
    [protectionBundle, addOnBundle].flatMap(
      (bundle) => bundle?.items?.map((item) => item.code) || [],
    ),
  );

  const individualItems = [...(protectionItems || []), ...(addOnItems || [])];

  const mappedIndividualItems = transformChildSeatItemsForPriceDetails(
    individualItems,
    itemsIncludedInBundles,
  ).map((item) => ({
    ...item,
    // if item is included in bundle, it can`t be removed separately
    removable: !itemsIncludedInBundles.has(item.code),
  })) as CostListType[];

  return [...mappedIndividualItems];
};

export const createRegionAriaReference =
  (label: string) => (accordionRoot: HTMLDivElement | null) => {
    if (!accordionRoot) return;

    const regionElement = accordionRoot.querySelector<HTMLElement>(
      '.MuiAccordion-region',
    );

    if (regionElement) {
      regionElement.setAttribute('aria-label', label);
    }
  };
